import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  ValidateNested,
} from 'class-validator';
import { GouvDvfDataDto } from '../gouv/gouv-dvf.dto';
import { GouvGeorisquesDto } from '../gouv/gouv-georisques.dto';

export type SitadelPressureLevel = 'low' | 'moderate' | 'high';
export type NoiseCategory = 'calm' | 'moderate' | 'exposed';
export type InseeAffluenceLevel = 'modest' | 'median' | 'affluent' | 'very_affluent';

export class PropertyGeospatialSitadelDto {
  @IsOptional()
  @IsNumber()
  unitsUnderConstruction?: number;

  @IsOptional()
  @IsNumber()
  permitsCount?: number;

  @IsOptional()
  @IsEnum(['low', 'moderate', 'high'])
  pressureLevel?: SitadelPressureLevel;

  @IsOptional()
  @IsNumber()
  radiusMeters?: number = 500;
}

export class PropertyGeospatialNoiseDto {
  @IsOptional()
  @IsNumber()
  levelDb?: number;

  @IsOptional()
  @IsEnum(['calm', 'moderate', 'exposed'])
  category?: NoiseCategory;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  mainSources?: string[] = [];
}

export class PropertyGeospatialInseeDto {
  @IsOptional()
  @IsNumber()
  medianIncome?: number;

  @IsOptional()
  @IsEnum(['modest', 'median', 'affluent', 'very_affluent'])
  affluenceLevel?: InseeAffluenceLevel;

  @IsOptional()
  @IsNumber()
  comparisonToCityPercent?: number;

  @IsOptional()
  @IsNumber()
  comparisonToDepartmentPercent?: number;

  @IsOptional()
  @IsNumber()
  communeMedianIncome?: number;
}

export class CadastralParcelDto {
  @IsNotEmpty()
  @IsString()
  reference!: string;

  @IsOptional()
  @IsString()
  section?: string;

  @IsOptional()
  @IsString()
  numero?: string;

  @IsOptional()
  @IsNumber()
  surface?: number;

  @IsOptional()
  @IsBoolean()
  isPrimary?: boolean;

  @IsOptional()
  @IsBoolean()
  hasBdnbBuilding?: boolean;

  @IsOptional()
  geometry?: unknown;
}

export class PropertyCadastralDto {
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CadastralParcelDto)
  parcels!: CadastralParcelDto[];

  @IsOptional()
  @IsNumber()
  totalSurface?: number;
}

export class CadastralParcelFeatureDto {
  @IsString()
  id!: string;

  @IsString()
  section!: string;

  @IsString()
  numero!: string;

  @IsOptional()
  @IsNumber()
  surface?: number;

  @IsOptional()
  @IsString()
  codeInsee?: string;

  @IsOptional()
  @IsString()
  commune?: string;

  @IsOptional()
  @IsBoolean()
  hasBdnbBuilding?: boolean;

  geometry!: unknown;
}

export class PropertyGeospatialBdnbDto {
  @IsOptional()
  @IsNumber()
  annee_construction?: number;

  @IsOptional()
  @IsString()
  mat_mur_txt?: string;

  @IsOptional()
  @IsString()
  mat_toit_txt?: string;

  @IsOptional()
  @IsNumber()
  nb_log?: number;

  @IsOptional()
  @IsNumber()
  nb_niveau?: number;

  @IsOptional()
  @IsNumber()
  hauteur_mean?: number;

  @IsOptional()
  @IsNumber()
  surface_emprise_sol?: number;

  @IsOptional()
  @IsString()
  usage_niveau_1_txt?: string;
}

export class PropertyGeospatialFeaturesDto {
  @IsOptional()
  @ValidateNested()
  @Type(() => GouvDvfDataDto)
  dvf?: GouvDvfDataDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyGeospatialSitadelDto)
  sitadel?: PropertyGeospatialSitadelDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyGeospatialNoiseDto)
  noise?: PropertyGeospatialNoiseDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyGeospatialInseeDto)
  insee?: PropertyGeospatialInseeDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => GouvGeorisquesDto)
  geohazard?: GouvGeorisquesDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyCadastralDto)
  cadastral?: PropertyCadastralDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyGeospatialBdnbDto)
  bdnb?: PropertyGeospatialBdnbDto;
}

export class PropertyCreationGeospatialDto {
  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyCadastralDto)
  cadastral?: PropertyCadastralDto;
}
